/** @jsx jsx */
import { jsx } from '@emotion/react';
// eslint-disable-next-line no-unused-vars
import React from 'react';
import { PropertyListSelector } from 'components/selectors/propertyListSelector';
import { LimitSelector } from 'components/selectors/limitSelector';
import { ColorSelector } from 'components/selectors/colorSelector';
import { EndpointPropertySelector } from 'components/selectors/endpointPropertySelector';
import { EndpointParameterSelector } from 'components/selectors/endpointParameterSelector';
import { AGGREGATION_METHOD_VALUES, FONT_FAMILIES, SCALING_FUNCTIONS } from 'widgets/chartUtils';
import { PropertyType as SrsPropertyType } from 'srs-middleware';
import CommonKnobsContainer from 'views/common/commonKnobsContainer';
import { Tab, Tabs } from '@blueprintjs/core';
import {WidgetParameterSelector} from 'components/selectors/widgetParameterSelector'

/**
 * Custom renderer for the controls available to change the chart settings.
 *
 * Data Properties for this Widget:
 *   -- propertyList
 *   -- limit
 *
 * Chart Properties for this Widget:
 *
 * @param cardIndex
 * @param endpointQuery
 * @param flags
 * @param widgetConfiguration
 * @returns {*}
 * @constructor
 */
export const WordCloudKnobs = ({ cardIndex, endpointQuery, flags, widgetConfiguration }) => {
	const toValues = (n)=>{return{label:String(n),value:n}}
	return (
		<Tabs>
			<Tab
				id="data"
				title="Data"
				panel={
					<React.Fragment>
						<CommonKnobsContainer>
							<PropertyListSelector
								cardIndex={cardIndex}
								itemIndex={0}
								flags={flags}
								label="Count By"
								endpointQuery={endpointQuery}
							/>
							<LimitSelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								initialLimit={50}
								label={'Item Limit'}
								limitProperty="maxRoot"
							/>
						</CommonKnobsContainer>
						<CommonKnobsContainer>
							<EndpointPropertySelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								propertyName="aggregationProperty"
								types={[SrsPropertyType.Numeric]}
								label="Aggregation Property"
								isNullable={true}
							/>
							<EndpointParameterSelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								propertyName="aggregationMethod"
								label="Aggregation Method"
								values={AGGREGATION_METHOD_VALUES}
								defaultValue={AGGREGATION_METHOD_VALUES[0]}
							/>
						</CommonKnobsContainer>
					</React.Fragment>
				}
			/>
			<Tab
				id="style"
				title="Style"
				panel={
					<React.Fragment>
						<CommonKnobsContainer>
							<ColorSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								activeItem={widgetConfiguration?.colorScale}
								cy-test="color-using-dropdown-button"
							/>
							{/* Font */}
							<WidgetParameterSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								label="Font Family"
								propertyName={'font'}
								values={FONT_FAMILIES}
								defaultValue={FONT_FAMILIES[0]}
							/>
							{/* min font size */}
							<WidgetParameterSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								label="Minimum Font Size"
								propertyName={'minFontSize'}
								values={[0,2,4,8,16].map(toValues)}
								defaultValue={toValues(4)}
							/>
							{/* Num rotation orientations */}
							<WidgetParameterSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								label="Number of Text Rotation Orientations"
								propertyName={'numOrientations'}
								values={[0,2,4,8,16].map(toValues)}
								defaultValue={toValues(2)}
							/>
							{/* min angle */}
							<WidgetParameterSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								label="Minimum Angle of Rotation"
								propertyName={'minRotation'}
								values={[0,-45,-90,-135,-180,-225,-270].map(toValues)}
								defaultValue={toValues(-90)}
							/>
							{/* max angle */}
							<WidgetParameterSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								label="Maximum Angle of Rotation"
								propertyName={'maxRotation'}
								values={[0,45,90,135,180,225,270].map(toValues)}
								defaultValue={toValues(90)}
							/>
							{/* Size scaling function */}
							<WidgetParameterSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								label="Size Scaling Function"
								propertyName={'sizeScale'}
								values={SCALING_FUNCTIONS}
								defaultValue={SCALING_FUNCTIONS[0]}
							/>
						</CommonKnobsContainer>
					</React.Fragment>
				}
			/>
		</Tabs>
	);
};
